import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { PartRepository } from "@partikelart/parts/data-access-core";
import type { CreatePartInput, Part } from "@partikelart/parts/domain";
import { map, Observable } from "rxjs";

import { PARTS_API_CONFIG } from "./parts-api-config";

/**
 * HTTP implementation of PartRepository.
 */
@Injectable()
export class CloudPartRepository implements PartRepository {
	private readonly config = inject(PARTS_API_CONFIG);

	constructor(private readonly http: HttpClient) {}

	private get baseUrl(): string {
		return `${this.config.portalBackendUrl ?? "http://localhost:3000"}/api`;
	}

	/** GET /api/parts -- list parts, optional siteId filter */
	getAll(siteId?: string): Observable<Part[]> {
		const params = siteId ? new HttpParams().set("siteId", siteId) : undefined;
		return this.http.get<Part[]>(`${this.baseUrl}/parts`, { params });
	}

	/** GET /api/parts/:id -- single part */
	getById(id: string): Observable<Part | null> {
		return this.http.get<Part>(`${this.baseUrl}/parts/${id}`).pipe(map((p) => p ?? null));
	}

	/** GET /api/parts/by-part-number/:partNumber -- lookup by part number */
	getByPartNumber(partNumber: string): Observable<Part | null> {
		return this.http
			.get<Part>(`${this.baseUrl}/parts/by-part-number/${encodeURIComponent(partNumber)}`)
			.pipe(map((p) => p ?? null));
	}

	/** POST /api/parts -- create part */
	create(input: CreatePartInput): Observable<Part> {
		return this.http.post<Part>(`${this.baseUrl}/parts`, input);
	}

	/** PUT /api/parts/:id -- update part */
	update(id: string, changes: Partial<Part>): Observable<Part> {
		return this.http.put<Part>(`${this.baseUrl}/parts/${id}`, changes);
	}

	/** DELETE /api/parts/:id -- delete part */
	delete(id: string): Observable<void> {
		return this.http.delete<void>(`${this.baseUrl}/parts/${id}`);
	}
}
